import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as API from 'api/baseAPI';
import { setGlobalLoading } from 'redux/appSlice';
import RsvpFormPreview from './RsvpFormPreview';
import { getEnabledLocales } from './detailsSchema';
import { Box, ThemeProvider, createTheme } from '@mui/material';
import EventPreview from './EventPreview';
import RsvpFormFinish from './RsvpFormFinish';
import { FormProvider, useForm } from 'react-hook-form';
import { getMultiLocaleContent } from 'utils/helper';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import RsvpFormPayment from './RsvpFormPayment';
import { resolveColorTheme } from 'utils/colorThemeHelpers';
const mainColor = '#3FBB9B';
const textColor = '#ffffff';

export default function Register(props) {
  const dispatch = useDispatch();
  const params = useParams();
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const rsvpRef = useRef({});
  const eventRef = useRef({});

  // Full-page background follows the event's color theme (same resolution as
  // the EventPreview card; undefined → sx drops it → plain page as before).
  const colorTheme = resolveColorTheme(eventRef.current);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          // Form controls (Back/Next, checkbox, focus states) follow the
          // event accent — the same link/button colors the card uses.
          primary: {
            main: colorTheme.linkButtonColor || mainColor,
            contrastText: colorTheme.buttonTextColor || textColor,
          },
          // Themed events render on the event backgroundColor — form text
          // follows the theme textColor so steps stay readable.
          ...(colorTheme.textColor
            ? { text: { primary: colorTheme.textColor, secondary: colorTheme.textColor } }
            : {}),
        },
        components: {
          MuiOutlinedInput: {
            defaultProps: {
              startAdornment: <Box sx={{ ml: '-12.5px' }} />,
            },
            styleOverrides: {
              root: {
                maxHeight: '40px',
                // Text boxes / selects are white fields with black text —
                // they read as form fields on any page background. Outlines
                // stay MUI default grey; focus + hover follow the event
                // accent via palette.primary.
                backgroundColor: '#fff',
                color: '#000',
                '& .MuiInputBase-input': {
                  paddingLeft: '12px',
                },
              },
            },
          },
        },
      }),
    [colorTheme.linkButtonColor, colorTheme.buttonTextColor, colorTheme.textColor]
  );
  const registeredSuccessResponse = useRef({});

  const [initializedEvent, setInitializedEvent] = useState(false);

  useEffect(() => {
    if (!params.eventId) {
      return;
    }
    dispatch(setGlobalLoading(true));

    API.getEvent(params.eventId)
      .then((response) => {
        if (response && response.data) {
          if (typeof response.data !== 'object') {
            throw new Error('Invalid Event');
          }
          API.getEventV2(params.eventId)
            .then((response) => {
              if (response && response.data) {
                if (typeof response.data !== 'object') {
                  throw new Error('Invalid Event');
                }
                if (!response.data.rsvp) {
                  setNotFound(true);
                  dispatch(setGlobalLoading(false));
                  return;
                }
                rsvpRef.current = response.data.rsvp;
                eventRef.current = response.data;
                setInitializedEvent(true);
                dispatch(setGlobalLoading(false));
              }
            })
            .catch((err) => {
              // logger.error(err);
              setNotFound(true);
              dispatch(setGlobalLoading(false));
            });
        } else {
          throw new Error('Invalid Event');
        }
      })
      .catch(() => {
        setNotFound(true);
      });
  }, [dispatch, params.eventId]);

  const [activeStep, setActiveStep] = useState(0);
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [activeStep]);

  const methods = useForm();
  const { i18n } = useTranslation();

  // Locales the event's language switch offers — the same rule the landing tab
  // strip uses (detailsSchema.getEnabledLocales). Computed once here and passed
  // to every step, so no step computes it and steps 0 and 1-4 agree.
  const enabledLocales = getEnabledLocales(eventRef.current);

  useEffect(() => {
    if (initializedEvent) {
      const title = getMultiLocaleContent(eventRef.current.title, i18n.language);
      if (title !== '') {
        document.title = `${title} | ${process.env.REACT_APP_DEFAULT_DOCUMENT_TITLE}`;
      } else {
        document.title = process.env.REACT_APP_DEFAULT_DOCUMENT_TITLE;
      }
    } else {
      document.title = process.env.REACT_APP_DEFAULT_DOCUMENT_TITLE;
    }
  }, [initializedEvent, i18n.language]);

  return (
    <Fragment>
      {notFound ? (
        <main>
          <div style={{ padding: '10px' }}>
            <p>{i18n.t('register.page-not-found')}</p>
          </div>
        </main>
      ) : (
        <Fragment>
          {initializedEvent && (
            <ThemeProvider theme={muiTheme}>
              <Box
                sx={{
                  minHeight: '100vh',
                  backgroundColor: colorTheme.backgroundColor,
                  color: colorTheme.textColor,
                }}
              >
                <Box
                  sx={{
                    maxWidth: '900px',
                    textAlign: 'start',
                    display: 'flex',
                    flexDirection: 'column',
                    m: 'auto',
                  }}
                >
                  {/* CONTENT */}
                  <FormProvider {...methods}>
                    <Box id="rForm" component="form">
                      {activeStep === 0 && (
                        <EventPreview
                          record={{ ...eventRef.current, rsvp: rsvpRef.current }}
                          onJoinClick={() => {
                            setActiveStep(1);
                          }}
                        />
                      )}
                      {activeStep === 1 && (
                        <RsvpFormPreview
                          record={{ ...eventRef.current, rsvp: rsvpRef.current }}
                          locales={enabledLocales}
                          onBackClick={() => {
                            setActiveStep(0);
                          }}
                          onSubmitClick={() => {
                            const result = document?.getElementById('rForm')?.reportValidity();
                            if (result) {
                              setActiveStep(2);
                            }
                          }}
                        />
                      )}
                      {activeStep === 2 && (
                        <RsvpFormPreview
                          isConfirmMode
                          record={{ ...eventRef.current, rsvp: rsvpRef.current }}
                          locales={enabledLocales}
                          onBackClick={() => {
                            setActiveStep(1);
                          }}
                          onSubmitClick={(formData) => {
                            if (submitting) {
                              return;
                            }
                            setSubmitting(true);

                            const filterEmptyValue = Object.fromEntries(
                              Object.entries(formData).filter(
                                ([_, v]) => typeof v === 'boolean' || (v && v.length > 0)
                              )
                            );
                            const emailTypeField = rsvpRef.current.fields.find(
                              (it) => it.type === 'email'
                            );
                            // console.log('emailTypeField', emailTypeField);
                            if (!emailTypeField) {
                              alert(t('register. error-fail-email-not-found'));
                              setSubmitting(false);
                              return;
                            }
                            const email = filterEmptyValue[emailTypeField.key];
                            if (rsvpRef.current?.payment?.enabled) {
                              API.postUserRegisterPayment(params.eventId, email, filterEmptyValue)
                                .then((resp) => {
                                  registeredSuccessResponse.current = resp.data;
                                  setActiveStep(4);
                                  setSubmitting(false);
                                })
                                .catch((err) => {
                                  if (err?.response?.status === 409) {
                                    alert(t('register.error-email-registered'));
                                    setSubmitting(false);
                                    return;
                                  }
                                  if (err?.response?.data?.code === 'EVENT_CLOSED') {
                                    alert(t('register.error-event-closed'));
                                    setSubmitting(false);
                                    return;
                                  }
                                  alert(t('register.error-fail-register'));
                                  setSubmitting(false);
                                });
                            } else {
                              API.postUserRegister(params.eventId, email, filterEmptyValue)
                                .then((resp) => {
                                  registeredSuccessResponse.current = resp.data;
                                  setActiveStep(3);
                                  setSubmitting(false);
                                })
                                .catch((err) => {
                                  if (err?.response?.status === 409) {
                                    alert(t('register.error-email-registered'));
                                    setSubmitting(false);
                                    return;
                                  }
                                  if (err?.response?.data?.code === 'EVENT_CLOSED') {
                                    alert(t('register.error-event-closed'));
                                    setSubmitting(false);
                                    return;
                                  }
                                  alert(t('register.error-fail-register'));
                                  setSubmitting(false);
                                });
                            }
                          }}
                        />
                      )}
                      {activeStep === 3 && (
                        <RsvpFormFinish
                          record={{ ...eventRef.current, rsvp: rsvpRef.current }}
                          locales={enabledLocales}
                          registeredSuccessResponse={registeredSuccessResponse.current}
                        />
                      )}
                    </Box>
                    {activeStep === 4 && (
                      <Box>
                        <RsvpFormPayment
                          record={{ ...eventRef.current, rsvp: rsvpRef.current }}
                          locales={enabledLocales}
                          registeredSuccessResponse={registeredSuccessResponse.current}
                          formData={methods.getValues()}
                          setActiveStep={setActiveStep}
                        />
                      </Box>
                    )}
                  </FormProvider>
                </Box>
              </Box>
            </ThemeProvider>
          )}
        </Fragment>
      )}
    </Fragment>
  );
}
